import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../lib/api";
import { LOGO_URL } from "../lib/brand";
import { L, ROTATING } from "../lib/landingContent";
import { CountUp } from "../components/common";
import {
  Sprout, TrendingUp, CloudRain, FlaskConical, Landmark, Leaf, ArrowRight,
  CheckCircle2, Globe, LogIn, ChevronDown, ShoppingCart, Bug, GraduationCap,
  Calculator, Users, MapPin, ShieldCheck, ChevronRight, PlayCircle, Menu, X, Eye,
} from "lucide-react";

const HERO = "/hero-farmer.webp";
const ICONS = { sprout: Sprout, trending: TrendingUp, cloud: CloudRain, flask: FlaskConical, landmark: Landmark, cart: ShoppingCart, bug: Bug, graduation: GraduationCap, calculator: Calculator, users: Users, mappin: MapPin, shield: ShieldCheck };
const TONES = { green: "bg-green-100 text-green-600", amber: "bg-amber-100 text-amber-600", sky: "bg-sky-100 text-sky-600", orange: "bg-orange-100 text-orange-600", yellow: "bg-yellow-100 text-yellow-600" };
const reduce = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function FloatCard({ tone, Icon, title, main, sub, cls, testId }) {
  return (
    <div className={`${cls} bg-white/90 backdrop-blur border rounded-2xl shadow-lg p-3 flex items-center gap-3 w-64`} data-testid={testId}>
      <div className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="font-bold text-sm leading-tight truncate">{main}</p>
        <p className="text-[11px] text-muted-foreground truncate">{sub}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto shrink-0" />
    </div>
  );
}

export default function Landing() {
  const { language, setLanguage } = useApp();
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const t = L[language] || L["हिंदी"];
  const words = ROTATING[language] || ROTATING["हिंदी"];
  const [wi, setWi] = useState(0);
  const [data, setData] = useState(null);
  const [mi, setMi] = useState(0);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => { setWi(0); }, [language]);
  useEffect(() => {
    const id = setInterval(() => setWi((i) => (i + 1) % words.length), 2500);
    return () => clearInterval(id);
  }, [words.length]);
  useEffect(() => {
    fetch(`${API_BASE}/public/landing`).then((r) => r.json()).then(setData).catch(() => {});
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (!data?.ticker?.length) return;
    const id = setInterval(() => setMi((i) => (i + 1) % data.ticker.length), 2200);
    return () => clearInterval(id);
  }, [data]);

  const goStart = () => navigate("/onboarding");
  const startTour = async () => { try { await demoLogin(); navigate("/?tour=1"); } catch { navigate("/login"); } };
  const goFeature = async (route) => { try { await demoLogin(); navigate(route); } catch { navigate("/login"); } };
  const NAV_ROUTES = { market: "/market", weather: "/weather", schemes: "/schemes", ai: "/ai", knowledge: "/resources" };
  const handleNav = (key) => {
    if (key === "home") return window.scrollTo({ top: 0, behavior: "smooth" });
    if (key === "about") return document.getElementById("about-section")?.scrollIntoView({ behavior: "smooth" });
    if (NAV_ROUTES[key]) return goFeature(NAV_ROUTES[key]);
  };
  const mCrop = data?.ticker?.[mi];
  const langs = ["हिंदी", "English", "Hinglish"];

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] font-deva overflow-x-hidden">
      {/* NAVBAR */}
      <header className={`sticky top-0 z-50 transition-all ${scrolled ? "glass shadow-md" : "bg-white shadow-sm"}`} data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="Agro Madat" className="h-9 w-9 rounded-xl object-contain" loading="eager" />
            <div><p className="font-bold leading-tight"><span className="text-green-600">Agro</span> Madat</p><p className="text-[10px] text-muted-foreground leading-tight">{t.tagline}</p></div>
          </div>
          <nav className="hidden lg:flex items-center gap-1 glass rounded-full border px-2 py-1.5 shadow-sm">
            {Object.entries(t.nav).map(([key, label], i) => (
              <button key={key} onClick={() => handleNav(key)} data-testid={`nav-${key}`}
                className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${i === 0 ? "bg-green-600 text-white" : "hover:bg-green-50 hover:text-green-700"}`}>{label}</button>
            ))}
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            {/* desktop language + login */}
            <div className="relative hidden lg:block">
              <button onClick={() => setLangOpen((v) => !v)} data-testid="landing-lang" className="flex items-center gap-1 px-3 py-2 rounded-full border text-sm tap"><Globe className="h-4 w-4" />{language}<ChevronDown className="h-3 w-3" /></button>
              {langOpen && (
                <div className="absolute right-0 mt-1 bg-card border rounded-xl shadow-lg py-1 w-32 z-50">
                  {langs.map((l) => <button key={l} onClick={() => { setLanguage(l); setLangOpen(false); }} data-testid={`lang-${l}`} className={`block w-full text-left px-3 py-2 text-sm hover:bg-accent ${l === language ? "text-green-600 font-semibold" : ""}`}>{l}</button>)}
                </div>
              )}
            </div>
            <button onClick={goStart} data-testid="landing-login" className="hidden lg:flex items-center gap-1.5 px-5 py-2 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-700 tap"><LogIn className="h-4 w-4" />{t.login}</button>
            {/* mobile hamburger */}
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="landing-menu" aria-label="Menu" className="lg:hidden h-10 w-10 rounded-full border flex items-center justify-center tap">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU PANEL */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden border-t bg-white" data-testid="mobile-menu">
              <div className="px-4 py-3 space-y-1">
                {Object.entries(t.nav).map(([key, label]) => (
                  <button key={key} onClick={() => { setMenuOpen(false); handleNav(key); }} data-testid={`m-nav-${key}`}
                    className="flex items-center justify-between w-full text-left px-3 py-2.5 rounded-xl text-[15px] font-medium hover:bg-green-50 hover:text-green-700 tap">
                    {label}<ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
                <div className="flex gap-1.5 pt-2">
                  {langs.map((l) => (
                    <button key={l} onClick={() => setLanguage(l)} data-testid={`m-lang-${l}`}
                      className={`flex-1 py-2 rounded-xl border text-xs font-medium tap ${l === language ? "border-green-600 bg-green-50 text-green-700" : "text-muted-foreground"}`}>{l}</button>
                  ))}
                </div>
                <button onClick={() => { setMenuOpen(false); goStart(); }} data-testid="m-login"
                  className="flex items-center justify-center gap-1.5 w-full mt-2 px-5 py-3 rounded-2xl bg-green-600 text-white font-semibold hover:bg-green-700 tap"><LogIn className="h-4 w-4" />{t.login}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* FULL-BLEED HERO */}
      <section className="relative" data-testid="landing-hero">
        <div className="absolute inset-0 bg-cover hidden lg:block" style={{ backgroundImage: `url(${HERO})`, backgroundPosition: "right top" }} aria-hidden />
        {/* desktop: white gradient from left for text readability */}
        <div className="absolute inset-0 hidden lg:block" style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 30%, rgba(255,255,255,0.4) 52%, rgba(255,255,255,0) 70%)" }} aria-hidden />
        {/* mobile: clean soft green→white background (photo shown fully in a framed card below) */}
        <div className="absolute inset-0 lg:hidden bg-gradient-to-b from-emerald-50 via-white to-white" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:min-h-[620px] grid lg:grid-cols-2 gap-6 items-center">
          {/* LEFT TEXT */}
          <div className="pt-8 pb-6 lg:py-12">
            <span className="inline-block px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-sm font-medium mb-5" data-testid="hero-badge">{t.badge}</span>
            <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.12]">
              <span className="block">{t.heroL1}</span>
              <span className="inline-flex items-baseline h-[1.2em] overflow-hidden relative" data-testid="rotating-word">
                <AnimatePresence mode="wait">
                  <motion.span key={`${language}-${wi}`} className="inline-block text-green-600"
                    initial={reduce ? { opacity: 0 } : { y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { y: "-100%", opacity: 0 }}
                    transition={{ duration: 0.45, ease: [0.23, 1, 0.32, 1] }}>
                    {words[wi]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <span className="block">{t.heroL3}</span>
            </h1>
            <p className="text-muted-foreground mt-5 max-w-md text-base sm:text-lg">{t.subtext}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={goStart} data-testid="hero-cta-primary" className="group flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-green-600 text-white font-semibold hover:bg-green-700 tap shadow-lg shadow-green-600/25"><Leaf className="h-4 w-4" />{t.ctaPrimary}<ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></button>
              <button onClick={startTour} data-testid="hero-cta-demo" className="flex items-center gap-2 px-6 py-3.5 rounded-2xl border-2 bg-white/70 font-semibold hover:bg-white tap"><PlayCircle className="h-5 w-5 text-green-600" />{t.ctaSecondary}</button>
              <button onClick={() => navigate("/explore")} data-testid="hero-cta-guest" className="flex items-center gap-2 px-6 py-3.5 rounded-2xl border-2 border-green-600/30 bg-transparent text-green-700 font-semibold hover:bg-green-50 tap"><Eye className="h-5 w-5" />{language === "English" ? "Browse without login" : "बिना लॉगिन देखें"}</button>
            </div>
            <div className="flex flex-wrap gap-4 mt-5 text-sm text-muted-foreground">
              {t.trust.map((x, i) => <span key={i} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-green-600" />{x}</span>)}
            </div>

            {/* MOBILE HERO IMAGE — farmer fully visible in a framed card with live chips */}
            <motion.div className="lg:hidden mt-8 relative" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
              <div className="relative rounded-[28px] overflow-hidden shadow-2xl ring-1 ring-black/5">
                <img src={HERO} alt="Indian farmer" loading="eager" className="w-full h-[320px] object-cover object-[center_18%]" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.28) 100%)" }} aria-hidden />
                {/* weather chip */}
                <div className="absolute top-3 left-3 flex items-center gap-2 rounded-2xl bg-white/92 backdrop-blur px-3 py-2 shadow-lg" data-testid="m-hero-weather">
                  <span className="h-8 w-8 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0"><CloudRain className="h-4 w-4" /></span>
                  <div><p className="text-[10px] text-muted-foreground leading-none">{t.cards.weather.t}</p><p className="font-bold text-sm leading-tight">{data?.weather?.temp ?? 28}°C</p></div>
                </div>
                {/* mandi chip */}
                <div className="absolute bottom-3 right-3 flex items-center gap-2 rounded-2xl bg-white/92 backdrop-blur px-3 py-2 shadow-lg" data-testid="m-hero-mandi">
                  <span className="h-8 w-8 rounded-full bg-green-500 text-white flex items-center justify-center shrink-0"><TrendingUp className="h-4 w-4" /></span>
                  <div><p className="text-[10px] text-muted-foreground leading-none">{t.cards.mandi.t}</p><p className="font-bold text-[13px] leading-tight">{mCrop?.commodity || "गेहूँ"} <span className="text-green-600">₹{mCrop?.modal?.toLocaleString("en-IN") || "2,415"}</span></p></div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* RIGHT FLOATING CARDS */}
          <div className="hidden lg:flex flex-col items-end gap-4 py-12 relative z-10">
            <FloatCard testId="float-weather" cls="floaty" tone="bg-green-500 text-white" Icon={CloudRain}
              title={t.cards.weather.t} main={`${data?.weather?.temp ?? 28}°C`} sub={language === "English" ? (data?.weather?.condition_en || t.cards.weather.s) : t.cards.weather.s} />
            {/* Mandi card — auto-cycles crops for authentic live feel */}
            <div className="floaty-2 bg-white/90 backdrop-blur border rounded-2xl shadow-lg p-3 flex items-center gap-3 w-64" data-testid="float-mandi">
              <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0 bg-green-500 text-white"><TrendingUp className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-muted-foreground">{t.cards.mandi.t}</p>
                <AnimatePresence mode="wait">
                  <motion.div key={mi} initial={reduce ? { opacity: 0 } : { y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={reduce ? { opacity: 0 } : { y: -8, opacity: 0 }} transition={{ duration: 0.35 }}>
                    <p className="font-bold text-sm leading-tight truncate">{mCrop?.commodity || "गेहूँ"} <span className="text-green-600">₹{mCrop?.modal?.toLocaleString("en-IN") || "2,415"}</span><span className="text-[10px] text-muted-foreground">/क्विंटल</span></p>
                    <p className="text-[11px] truncate">{mCrop?.market ? <span className="text-muted-foreground">{mCrop.market} · </span> : null}<span className={mCrop?.trend === "up" ? "text-green-600" : "text-red-500"}>{mCrop?.trend === "up" ? "↗" : "↘"} {t.cards.mandi.s}</span></p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </div>
            <FloatCard testId="float-schemes" cls="floaty-3" tone="bg-orange-500 text-white" Icon={Landmark}
              title={t.cards.schemes.t} main={`${data?.stats?.schemes || 19} ${t.available}`} sub={t.cards.schemes.s} />
            <FloatCard testId="float-ai" cls="floaty" tone="bg-purple-500 text-white" Icon={Sprout}
              title={t.cards.ai.t} main={t.cards.ai.s} sub="" />
          </div>
        </div>
      </section>

      {/* SERVICES STRIP (8) overlapping hero bottom */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-6 lg:-mt-10 relative z-20">
        <div className="bg-white border rounded-3xl shadow-xl p-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-5" data-testid="services-strip">
          {t.services.map((s, i) => {
            const Icon = ICONS[s.icon] || Sprout;
            return (
              <div key={i} className="text-center group cursor-default">
                <div className={`h-14 w-14 rounded-2xl mx-auto flex items-center justify-center mb-2 ${TONES[s.tone]} transition-transform group-hover:scale-110 group-hover:-rotate-6`}><Icon className="h-6 w-6" /></div>
                <p className="font-semibold text-sm leading-tight">{s.t}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* LIVE TICKER */}
      {data?.ticker?.length > 0 && (
        <div className="mt-10 bg-green-600 text-white py-2.5 overflow-hidden marquee-wrap" data-testid="ticker">
          <div className="marquee-track flex gap-8 whitespace-nowrap w-max">
            {[...data.ticker, ...data.ticker].map((r, i) => (
              <span key={i} className="text-sm font-medium">{r.commodity} ₹{r.modal?.toLocaleString("en-IN")}/{r.unit || "क्विंटल"}{r.market ? ` · ${r.market}` : ""} {r.trend === "up" ? "↗" : "↘"}</span>
            ))}
          </div>
        </div>
      )}

      {/* STATS BAND (4) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-green-50/60 rounded-3xl p-8 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-section">
          {t.stats.map((s, i) => {
            const Icon = ICONS[s.icon] || Sprout;
            return (
              <div key={i} className="flex items-center gap-3 justify-center sm:justify-start">
                <div className="h-12 w-12 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center shrink-0"><Icon className="h-6 w-6" /></div>
                <div>
                  <p className="text-2xl font-extrabold text-green-700">{s.text ? s.text : <><CountUp value={s.n} format={s.disp ? () => s.disp : undefined} />{s.suf || ""}</>}</p>
                  <p className="text-sm text-muted-foreground">{s.l}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* BOLD STATEMENT */}
      <section id="about-section" className="bg-[#fafaf5] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
            {t.statementPre}{" "}
            <span className="relative inline-block">
              <span className="relative z-10">{t.statementHi}</span>
              <motion.span className="absolute inset-x-0 bottom-1 h-3.5 bg-yellow-300/70 -z-0 origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2 }} />
            </span>{" "}
            {t.statementPost}
          </h2>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            {t.microTrust.map((x, i) => <span key={i} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-green-600" />{x}</span>)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-green-900 text-green-50 py-10" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-2"><img src={LOGO_URL} alt="" className="h-9 w-9 rounded-xl bg-white p-0.5" /><span className="font-bold">Agro Madat</span></div>
            <p className="text-sm text-green-200">{t.tagline}</p>
          </div>
          <div className="text-sm space-y-1">
            {Object.entries(t.nav).map(([key, label]) => <button key={key} onClick={() => handleNav(key)} data-testid={`footer-nav-${key}`} className="block text-left text-green-200 hover:text-white">{label}</button>)}
          </div>
          <div className="text-sm">
            <p className="text-green-200">{t.helpline}: <span className="font-semibold text-white">1800-180-1551</span></p>
            <p className="mt-3 text-green-300">{t.footerMade}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
